import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Flex,
  Text,
  Spinner,
  useToast
} from '@chakra-ui/react'
import { addDays, format } from 'date-fns'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { FaCheckCircle } from 'react-icons/fa'
import { decodeToken } from 'react-jwt'
import { useImmer } from 'use-immer'

import TableGrid from '@component/Layout/TableGrid'

import suppliers from '@helper/suppliers'
import mrp from '@service/mrp'

import { Pagination } from '@base/@types/providers/Parametrization/RevisionProvider'
import sapHistoricHeader from '@base/variables/headers/suppliers/saphistoric'

import ExportSapHistoric from './ExportSapHistoric'

interface IActionSapHistoricWithTrigger {
  onSuccess?: () => void
  triggerModal?: (code: string) => void
  code: any
  isOpen?: boolean
  onClose?: () => void
  successMessage?: string
}

const ActionSapHistoric: FC<IActionSapHistoricWithTrigger> = ({
  code = '',
  onSuccess,
  isOpen,
  onClose,
  successMessage
}) => {
  const toast = useToast()

  const headers = useMemo(() => sapHistoricHeader, [])

  const [rows, updateRows] = useImmer<any[]>([])
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })

  const [loading, setLoadingSupply] = useState(false)
  const [error, setError] = useState(false)
  const [showSuccessScreen, setShowSuccessScreen] = useState(true)
  const [modalCode] = useState(code)
  // Lido uma vez: recriado a cada render invalidava os useCallback que dependem dele
  const decodedToken = useMemo(() => {
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE
    const userInfos = JSON.parse(
      localStorage.getItem(
        `@@auth0spajs@@::${clientId}::${audience}::openid profile email offline_access`
      )
    )
    return decodeToken(userInfos?.body.access_token) as any
  }, [])
  const getData = useCallback(
    async (page = 0) => {
      setLoadingSupply(true)
      setError(false)
      const idUser = decodedToken?.userid
      if (!code) {
        setLoadingSupply(false)
        setError(true)
        toast({
          title: 'Erro',
          description: 'Código do pedido não definido.',
          status: 'error',
          duration: 5000,
          isClosable: true
        })
        return
      }
      // Data local (toISOString usa UTC e muda o dia após as 21h no Brasil)
      const today = format(new Date(), 'yyyy-MM-dd')
      const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd')

      try {
        const {
          data: { data, pagination }
        } = await mrp.getHistoricSapProtocol(
          code,
          page,
          50,
          today,
          tomorrow,
          idUser
        )
        updatePagination(pagination)
        updateRows(draft => {
          return draft
            .filter((row: any) => row.id === 'null')
            .concat(suppliers.parseResultHistoric(data))
        })
      } catch (ex: any) {
        setError(true)
        const message =
          ex.response?.status === 400
            ? ex.response?.data?.message
            : 'Ocorreu um erro ao tentar recuperar o histórico de pedidos.'
        toast({
          title: 'Atenção.',
          description: message,
          status: ex.response?.status === 400 ? 'warning' : 'error',
          duration: 2000,
          isClosable: true
        })
      }

      setLoadingSupply(false)
    },
    [code, updatePagination, updateRows, toast, decodedToken]
  )

  const handleVisualize = () => {
    setShowSuccessScreen(false)
    // openModal() // Removed as isOpen is controlled externally
    if (code) {
      getData(0)
    } else {
      toast({
        title: 'Aviso',
        description:
          'Código do pedido não definido para visualizar o histórico.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
  }
  useEffect(() => {
    if (onSuccess && isOpen) {
      onSuccess()
    }
  }, [isOpen, onSuccess])
  // Call getData when modal opens and modalCode is available
  useEffect(() => {
    if (isOpen && code) {
      setShowSuccessScreen(true)
    }
  }, [isOpen, code])

  return (
    <>
      {/* Still keep the button for manual trigger */}
      {/* <Button onClick={handleVisualize}>Show Modal</Button> */}

      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent transition="all ease .3s">
          <ModalHeader bgColor="white">
            <Flex>
              <Text pt={2} pr={2}>
                Pedidos Criados Hoje
              </Text>
              <>
                {!showSuccessScreen && (
                  <>
                    <ExportSapHistoric code={code} days={0} />
                  </>
                )}
                {/* {!showSuccessScreen && (
                  <>
                    <ExportSapHistoric code={code} days={0} />
                  </>
                )} */}
              </>
            </Flex>
          </ModalHeader>

          <ModalCloseButton />
          <ModalBody bgColor="white">
            {showSuccessScreen ? (
              <Flex
                flexDir="column"
                alignItems="center"
                justifyContent="center"
                h="380px"
              >
                <FaCheckCircle size={64} color="#38a169" />
                <Text
                  mb={4}
                  mt={4}
                  textAlign="center"
                  fontSize="xl"
                  fontWeight="bold"
                >
                  {successMessage || 'Pedido Criado com Sucesso!'}
                </Text>
                <Text mb={4} textAlign="center">
                  Os Pedidos realizados no dia de Hoje
                </Text>
                <Button onClick={handleVisualize}>Visualizar</Button>
              </Flex>
            ) : error ? (
              <Flex
                flexDir="column"
                alignItems="center"
                justifyContent="center"
                h="380px"
              >
                <Text mb={4}>Ocorreu um erro ao tentar recuperar os dados</Text>
                <Button
                  onClick={() =>
                    modalCode
                      ? getData(0)
                      : toast({
                          title: 'Aviso',
                          description: 'Código do pedido não definido.',
                          status: 'warning',
                          duration: 3000,
                          isClosable: true
                        })
                  }
                >
                  Tentar novamente
                </Button>
              </Flex>
            ) : loading ? (
              <Flex alignItems="center" justifyContent="center" h="380px">
                <Spinner />
              </Flex>
            ) : (
              <TableGrid
                id="table-suppliers"
                headers={headers}
                data={rows}
                height="360px"
                pagination={pagination}
                udatePageChange={getData}
                isPagedBack={true}
                isPaged={false}
              />
            )}
          </ModalBody>
          <ModalFooter justifyContent="center" bgColor="white"></ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ActionSapHistoric
